/**
 * MACRO WALLS — the state decision for the top strip's MACRO chip (R485(2b)).
 *
 * WHY THIS MODULE EXISTS AT ALL, rather than living in `api.ts` beside the fetch: `api.ts` carries
 * a TS parameter property, so Node's strip-only test runner CANNOT import it. A predicate parked
 * there is unarmable — stated in api.ts's own header, and it is why `feedError.ts` and `cvdLane.ts`
 * already sit out here. The fetch stays in `api.ts`; every DECISION a reader could be misled by
 * lives here, where an arm can drive it.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT THIS CHIP REPLACED, AND WHY THE OLD TEXT WAS FALSE IN TWO DIRECTIONS
 *
 * Until now MACRO was an entry in TopStrip's `ABSENT` array — the seventh state, rendering
 * `NO FEED · a cme_options walls route` with the note "the DATA exists and is nightly-fed; only
 * the route is missing". Measured at the served bundle by CC-nn (index-BY-NDKOy.js): the string
 * `macro/walls` appeared ZERO times. There was no fetch to fail. The absence was unconditional.
 *
 * That note is now wrong twice over:
 *   1. THE ROUTE EXISTS. `/api/macro/walls?root=ES` serves ok:true with real ES walls.
 *   2. ITS PRODUCT LIST IS SUPERSEDED. The old note promised "NQ/RTY/CL/GC/SI/6E/6A/6J/BTC walls".
 *      The route's own `coverage_basis` says ES only BY RULING (R81, re-issued R462(5): SPX-ONLY),
 *      and names the others OFF-PLAN — "not missing". Rendering them as pending would re-advertise
 *      off-plan work on the owner's surface, which is the thing the ruling exists to stop.
 *
 * ⇒ The retirement mechanism is the one the file already documents for REGIME: DELETE THE ENTRY.
 *   A slot leaves `ABSENT` by its route landing, not by special-casing the template.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * THE ONE DECISION THIS FILE EXISTS TO GET RIGHT: WHICH CLOCK THE CHIP SHOWS
 *
 * The payload carries TWO times and they disagree by design:
 *
 *     data_date   2026-08-21   ← the session the walls DESCRIBE
 *     mirrored_at 2026-08-22T19:58Z ← when our mirror last COPIED them
 *
 * On a Saturday the mirror runs every few minutes over Friday's book. A chip keyed on
 * `mirrored_at` reads "seconds old" over day-old walls — a FRESH TIMESTAMP ON STALE CONTENT.
 * That exact shape has bitten this estate twice this week: the drift gate served yesterday's ref
 * with a 30s-old stamp (R323), and `vendor_ts` = max(per-row time) made a 7h-old chain look
 * current (jj's age trap). Both were the same error and both cost hours.
 *
 * ⇒ THE CHIP RENDERS `data_date`. `mirrored_at` is plumbing and belongs in the title, never in
 *   the number a reader glances at. **An age is a property of the DATA, never of the copy.**
 *
 * AND WHEN THE SERVER SAYS IT CANNOT VOUCH FOR THE AGE, WE DO NOT INVENT ONE. `age_trusted:false`
 * means the server's own age arithmetic is unreliable; a chip that prints a confident "1d" over
 * that is asserting something nobody measured. It renders the date and withholds the age.
 */

/** One product's walls, as the route serves them. Fields we do not render are omitted here. */
export interface MacroWallsProduct {
  product: string;
  symbol: string;
  put_wall: number | null;
  call_wall: number | null;
  max_pain_strike: number | null;
  /** The session the walls describe. THE age basis. */
  data_date: string | null;
  /** The mirror run date; retained beside the vendor-labelled session date. */
  scrape_date: string;
  /** Served wall rows whose source carried no session label. Informational; never forces STALE. */
  session_undated_rows: number | null;
  /** When our mirror copied them. Plumbing — never the headline. */
  mirrored_at?: string | null;
  data_date_age_days?: number | null;
  stale?: boolean | null;
  /** false ⇒ the server cannot vouch for its own age arithmetic. Withhold the age, keep the date. */
  age_trusted?: boolean | null;
  /** Worker provenance: true = fallback, false = matrix, null = row served no sources. */
  on_fallback?: boolean | null;
  /** The worker's served source keys; null means the row serves no source census. */
  sources?: string[] | null;
}

export interface MacroWallsPayload {
  ok?: boolean;
  products?: MacroWallsProduct[] | null;
  /** Days before `data_date` is considered stale. Served so the client need not guess. */
  budget_days?: number | null;
  stale?: boolean | null;
  /** WallsVenueState from ORDER_WALLS_VENUE_CLOCK_1's worker; VenueState from any worker served before it. */
  venue_state?: WallsVenueState | import("./api").VenueState;
}

/**
 * ORDER_WALLS_VENUE_CLOCK_1: the walls door's venue_state is CME Globex's calendar with a ONCE-A-DAY verdict
 * in place of the session one: FRESH when data_date is on or after the trading session before the last
 * ES close's Chicago trade date (judged at now - grace_s), STALE otherwise. The prior-session rule is
 * weekend-only and therefore fails loud after an unmodelled holiday. It is never CLOSED.
 */
export interface WallsVenueState {
  venue: string | null;
  verdict: "FRESH" | "STALE";
  expected_data_date: string | null;
  grace_s: number;
  closure: import("./api").VenueState["closure"];
  last_close: string | null;
  next_open: string | null;
  /** The close the clock judged by (as of now - grace_s). */
  clock_close: string | null;
}

/**
 * The rendered states. Deliberately NOT a superset of the strip's other chips: `NO_FEED` is absent
 * from this union ON PURPOSE.
 *
 * ⚠ `NO FEED` MEANS "NOBODY BUILT THIS YET" AND THE READER HAS BEEN TRAINED TO IGNORE IT. TopStrip
 * says so in writing at the REGIME error branch: using it for a route that IS built and IS broken
 * is "the NO FEED lie with its sign flipped — it would hide a real outage in the one slot designed
 * to be ignorable". The route is built. This chip can never return to NO FEED, so the type does not
 * offer it.
 */
export type MacroWallsState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  /** The route answered and had nothing for us. NOT an error, and NOT a silent blank. */
  | { kind: "empty"; why: string }
  | {
      kind: "walls";
      symbol: string;
      putWall: number | null;
      callWall: number | null;
      maxPain: number | null;
      /** The session the walls describe — the string the chip shows. */
      dataDate: string | null;
      /** null when the server cannot vouch for it. A null age renders as NO AGE, never as 0. */
      ageDays: number | null;
      stale: boolean;
      source: "MATRIX" | "FALLBACK" | "UNKNOWN" | "UNSERVED";
      sources: string[] | null;
      /** Copy time. Title only. */
      mirroredAt: string | null;
    };

/**
 * Derive what the chip shows.
 *
 * `error` wins over `payload` deliberately: a stale success beside a live failure would let the
 * chip keep painting yesterday's walls through an outage, which is the fail-soft law 1 forbids.
 */
export function macroWallsState(
  payload: MacroWallsPayload | null | undefined,
  error: string | null | undefined,
): MacroWallsState {
  if (error) return { kind: "error", message: error };
  if (!payload) return { kind: "loading" };

  if (payload.ok === false) {
    return { kind: "empty", why: "the route answered but reported not-ok" };
  }

  const rows = payload.products ?? [];
  if (rows.length === 0) {
    // A served empty list is a TRUE answer about the mirror, not a fault. Say which.
    return { kind: "empty", why: "the route served no products" };
  }

  const p = rows[0];

  /**
   * ⚠ THE AGE IS WITHHELD, NOT ZEROED, WHEN UNTRUSTED. `?? 0` here would render "0d" — the
   * freshest possible claim — at exactly the moment the server said it could not be trusted.
   * That is the null-coerces-to-a-confident-value defect that put strike 200 in the spot field
   * this week (`rows[0].price` with a null distance coercing to at-the-money). Not repeating it
   * one chip over.
   */
  const ageDays = p.age_trusted === false ? null : (p.data_date_age_days ?? null);
  const source = !Object.prototype.hasOwnProperty.call(p, "on_fallback")
    ? "UNSERVED"
    : p.on_fallback === true
      ? "FALLBACK"
      : p.on_fallback === false
        ? "MATRIX"
        : "UNKNOWN";

  return {
    kind: "walls",
    symbol: p.symbol ?? p.product,
    putWall: p.put_wall ?? null,
    callWall: p.call_wall ?? null,
    maxPain: p.max_pain_strike ?? null,
    dataDate: p.data_date ?? null,
    ageDays,
    stale: p.stale === true || payload.stale === true,
    source,
    sources: Array.isArray(p.sources) ? p.sources : null,
    mirroredAt: p.mirrored_at ?? null,
  };
}

/**
 * The age label. Returns null when there is nothing honest to say, and the caller renders nothing
 * rather than a placeholder — an em-dash in an age slot reads as "measured, and it is small".
 */
export function macroAgeLabel(state: MacroWallsState): string | null {
  if (state.kind !== "walls") return null;
  if (state.ageDays == null) return null;
  if (state.ageDays <= 0) return "today";
  return state.ageDays === 1 ? "1d old" : `${state.ageDays}d old`;
}

/**
 * The title text. Every number a reader can see is sourced here, INCLUDING the copy time — which
 * is exactly where `mirrored_at` belongs: available to anyone who asks, absent from the glance.
 */
export function macroWallsTitle(state: MacroWallsState): string {
  switch (state.kind) {
    case "loading":
      return "MACRO walls — waiting for the first answer from /api/macro/walls";
    case "error":
      return `/api/macro/walls failed — ${state.message}`;
    case "empty":
      return `MACRO walls — ${state.why}. The route answered; this is not an outage.`;
    case "walls": {
      const parts = [
        `${state.symbol} walls for the ${state.dataDate ?? "unstated"} session`,
        state.putWall != null ? `put wall ${state.putWall}` : "put wall unserved",
        state.callWall != null ? `call wall ${state.callWall}` : "call wall unserved",
        state.maxPain != null ? `max pain ${state.maxPain}` : "max pain unserved",
      ];
      if (state.source === "FALLBACK") {
        parts.push(
          `walls from a fallback source — no oi_matrix_text among the served sources (${state.sources?.join(", ") ?? "unserved"}); the age is withheld because the source is a fallback`,
        );
      } else if (state.source === "UNKNOWN") {
        parts.push("sources unserved for this row — the source cannot be named");
      } else if (state.source === "UNSERVED") {
        parts.push("on_fallback unserved (the row carries no on_fallback key)");
      }
      if (state.ageDays == null && state.source !== "FALLBACK") {
        parts.push("age withheld — the server did not vouch for its own age arithmetic");
      }
      if (state.mirroredAt) {
        // Named as a COPY time so nobody reads it as freshness of the data itself.
        parts.push(`mirrored (copied, not observed) at ${state.mirroredAt}`);
      }
      parts.push("ES only by ruling — other CME products are off-plan, not missing");
      return parts.join(" · ");
    }
  }
}
